import { PropsWithChildren, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import type { Content } from '@plone/types';
import { searchContent } from '@plone/volto/actions';
import type { BlockWrapperDataProps } from '../BlockWrapper/BlockWrapper';
import { BlockWrapper } from '../BlockWrapper/BlockWrapper';
import { Loader } from '@designsystem/stories/02-atoms/Loader';
import {
  ButtonGroup,
  ButtonGroupProps,
} from '@designsystem/stories/03-molecules/ButtonGroup';
import { Accordeon } from '@designsystem/stories/04-organisms/Accordeon';
import { urls } from '@fhnw/config/urls';
import { normalizeLink } from '@fhnw/helpers/DesignSystem/normalizeContent';
import { messages } from './messages';

export interface ViewProps {
  data: BlockWrapperDataProps;
  content: Content;
  isEdit?: boolean;
  block: string;
  onChangeBlock: (blockId: string, newData: any) => void;
}

const initialState = {
  items: [],
  loading: false,
  loaded: false,
};

const ACTIONS = [
  {
    type: 'primary',
    label: messages.DegreePrograms,
    href: urls.DegreePrograms,
  },
  {
    type: 'primary',
    label: messages.ContinuingEducations,
    href: urls.ContinuingEducations,
  },
];

const Factory = (props: PropsWithChildren) => <>{props.children}</>;

const View = (props: ViewProps) => {
  const intl = useIntl();
  const { data } = props;
  const uid = useSelector(
    (state: { content: { data: Content } }) => state.content?.data?.UID,
  );
  const dispatch = useDispatch();
  const subrequest = `subtopics_${uid}`;

  const { items, loading, loaded } = useSelector(
    (state: { search: { subrequests: Record<string, typeof initialState> } }) =>
      state.search?.subrequests?.[subrequest] || initialState,
  );

  let updated = false;
  if (!data?.block_title) {
    data.block_title = intl.formatMessage(messages.Title);
    updated = true;
  }

  useEffect(() => {
    if (updated && props?.isEdit && props.onChangeBlock) {
      props.onChangeBlock(props.block, data);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loaded || loading) {
      return;
    }
    dispatch(
      searchContent(
        '/',
        {
          parent_topics: [uid],
          sort_on: 'sortable_title',
          metadata_fields: ['Title', 'Description', 'UID'],
        },
        subrequest,
      ) as any,
    );
  }, [dispatch, uid, subrequest, loaded, loading]);

  if (!loaded) {
    return (
      <div className="page__section page__section--inline">
        <div className="page__section-content">
          <Loader />
        </div>
      </div>
    );
  }

  if (!props?.isEdit && !items.length) {
    return null;
  }

  const converted = items?.map(
    (topic: { Title: string; Description: string; UID: string }) => {
      const item = {
        title: topic.Title,
        content: (
          <>
            {topic.Description}
            <ButtonGroup
              items={
                ACTIONS.map((action) => ({
                  ...action,
                  href:
                    normalizeLink(
                      action.href[intl.locale as keyof typeof action.href],
                    ) +
                    '?topic=' +
                    topic.UID,
                  label: intl.formatMessage(action.label),
                })) as ButtonGroupProps['items']
              }
            />
          </>
        ),
      };

      return item;
    },
  );

  return (
    <BlockWrapper
      isEdit={props?.isEdit}
      block={props?.block}
      onChangeBlock={props?.onChangeBlock}
      data={data}
      factory={Factory}
    >
      <Accordeon items={converted} />
    </BlockWrapper>
  );
};

export default View;
